import { useContext, useEffect, useState } from "react";
import { ListPageProps } from "./Index";
import { Operation, TableHeaders } from "../../../../../utils/Enum.types";
import { Title } from "../../../../../stories/Title/Title";
import { TableHeaderProps } from "../../../../../utils/types";
import {
  HEADER_TEXT_ALIGN,
  SLNO_TEXT_ALIGN,
  TABLE_ROW_HEIGHT,
} from "../../../../../styles/Variables";
import { GridColDef, GridValidRowModel } from "@mui/x-data-grid-pro";
import Edit from "../../../../../images/EditProfile.svg";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import DeleteModal from "../../../../../customComponents/DeleteModal";
import { GetTicketReasons } from "./queries/queries";
import {
  AtsTicketReasons,
  DeleteTicketReasonByIdResult,
  DeleteTicketReasonByIdVars,
  GetTicketReasonsResult,
  GetTicketReasonsVar,
  ReOrderTicketReasonResult,
  ReOrderTicketReasonVar,
} from "./queries/types";
import { useLazyQuery, useMutation } from "@apollo/client";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import { AppContext } from "../../../../../context/context";
import { payloadTypes } from "../../../../../context/reducer";
import { DeleteTicketReason, ReOrderTicketReason } from "./queries/mutations";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import Delete from "../../../../../images/Delete.svg";
import LoadingModal from "../../../../pages/LoadingModal";
import Reorder from "../../../../../images/ReOrder.svg";

interface DraggedItem {
  item: AtsTicketReasons;
  index: number;
}

const List = ({ setOperation, setMessage }: ListPageProps) => {
  const { customer_id } = useUrlParams();
  const { state, dispatch } = useContext(AppContext);
  const { user_details } = useLoggedInUserDetails();
  const [rows, setRows] = useState<GridValidRowModel[]>([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const groupInput = document.getElementsByName("priority");
  const [reorder, setReorder] = useState(false);
  const [localItems, setLocalItems] = useState<AtsTicketReasons[]>([]);
  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const [ReOrder, { loading: reorderLoading }] = useMutation<
    ReOrderTicketReasonResult,
    ReOrderTicketReasonVar
  >(ReOrderTicketReason, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const [getTicketReasons, { data, loading }] = useLazyQuery<
    GetTicketReasonsResult,
    GetTicketReasonsVar
  >(GetTicketReasons);

  const [deleteTicketReason, { loading: deleteLoading }] = useMutation<
    DeleteTicketReasonByIdResult,
    DeleteTicketReasonByIdVars
  >(DeleteTicketReason, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const dynamicHeaders: TableHeaderProps[] = [
    {
      headerName: "Sl",
      field: "id",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
    },
    {
      headerName: `Reason`,
      field: "reason",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      flex: 1,
      hideable: false,
    },
    {
      headerName: `Priority`,
      field: "priority",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      flex: 1,
      hideable: false,
    },
  ];

  const columns: GridColDef[] = [
    ...dynamicHeaders,
    {
      headerName: TableHeaders.ACTION,
      field: "actions",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      renderCell: (params) => {
        return (
          <>
            <img
              src={Edit}
              alt=""
              onClick={() => {
                dispatch({
                  type: payloadTypes.SET_TICKET_REASON_ID,
                  payload: {
                    ticketReasonId: params.row.ticketReasonId,
                  },
                });
                if (groupInput.length) groupInput[0].focus();
                setOperation(Operation.UPDATE);
              }}
            />
            <img
              src={Delete}
              alt=""
              onClick={() => {
                dispatch({
                  type: payloadTypes.SET_TICKET_REASON_ID,
                  payload: {
                    ticketReasonId: params.row.ticketReasonId,
                  },
                });
                setDeleteModal(!deleteModal);
              }}
            />
          </>
        );
      },
    },
  ];

  const HandleDelete = async () => {
    if (customer_id)
      try {
        setDeleteModal(!deleteModal);
        deleteTicketReason({
          variables: {
            reasons_id: state.ticketReasonId,
            user_details,
          },
          refetchQueries: [
            {
              query: GetTicketReasons,
              variables: {
                customer_id,
              },
            },
          ],
        }).then(({ data }) => {
          if (data) {
            setMessage({
              flag: true,
              message: "Ticket reason Deleted Sucessfully",
              operation: Operation.DELETE,
            });
          }
        });
      } catch (err) {
        console.error((err as Error).message);
      }
  };

  useEffect(() => {
    if (data && !loading) {
      setRows(
        data.GetTicketReasons.map((node, index) => ({
          id: index + 1,
          ticketReasonId: node.id,
          priority: node.priority,
          reason: node.reason,
        }))
      );
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    if (customer_id) {
      getTicketReasons({
        variables: {
          customer_id,
        },
      }).then(({ data }) => {
        if (data && data.GetTicketReasons) {
          setLocalItems(data.GetTicketReasons);
        }
      });
    }
  }, [customer_id, getTicketReasons]);

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    item: AtsTicketReasons,
    index: number
  ): void => {
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    dropIndex: number
  ): void => {
    e.preventDefault();

    if (draggedItem && draggedItem.index !== dropIndex) {
      const newItems: AtsTicketReasons[] = [...localItems];
      const draggedItemData: AtsTicketReasons = newItems[draggedItem.index];

      // Remove the dragged item
      newItems.splice(draggedItem.index, 1);

      // Insert at new position
      newItems.splice(dropIndex, 0, draggedItemData);

      setLocalItems(newItems);
    }

    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragLeave = (): void => {
    setDragOverIndex(null);
  };

  const handleSaveOrder = () => {
    if (customer_id) {
      ReOrder({
        variables: {
          customer_id,
          user_details,
          input: localItems.map((res, index) => ({
            reasonID: res.id,
            idx: index + 1,
          })),
        },
        refetchQueries: [
          {
            query: GetTicketReasons,
            variables: {
              customer_id,
            },
          },
        ],
      }).then(({ data }) => {
        if (data) {
          setMessage({
            flag: true,
            message: "Re-Order Successful",
            operation: Operation.UPDATE,
          });
          setReorder(false);
        }
      });
    }
  };

  return (
    <>
      <div className="master-data__list">
        <Title variant="subtitle1">{`List of Reason`}</Title>
        <div className="master-data__list--tableblock">
          {data && !loading ? (
            reorder ? (
              localItems.map((label, index) => {
                return (
                  <div
                    className="add-wp-ts__list--table--li"
                    draggable
                    onDragStart={(e) => handleDragStart(e, label, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <label>{label.reason}</label>
                    <div className="add-wp-ts__list--table--li--flex">
                      <span>{label.priority}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <StyledDatagrid
                columns={columns}
                rows={rows}
                rowHeight={TABLE_ROW_HEIGHT}
                disableRowSelectionOnClick
                hideFooter
              />
            )
          ) : null}
        </div>
        {reorder ? (
          <button
            className="add-wp-ts__list--re-order"
            disabled={reorderLoading}
            onClick={handleSaveOrder}
            type="button"
          >
            Save-order <img src={Reorder} alt="" />
          </button>
        ) : localItems.length > 1 ? (
          <button
            className="add-wp-ts__list--re-order"
            disabled={reorderLoading}
            onClick={() => setReorder(!reorder)}
            type="button"
          >
            Re-order <img src={Reorder} alt="" />
          </button>
        ) : (
          <></>
        )}
      </div>
      <DeleteModal
        id={state.categoryId}
        modalFlag={deleteModal}
        setModalFlag={setDeleteModal}
        handleDelete={HandleDelete}
      />
      <LoadingModal flag={deleteLoading || loading || reorderLoading} />
    </>
  );
};

export default List;
